import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const tools = [
    { name: "Figma", logo: "/assets/tools/figma.svg", tag: "Interface Design", glow: "hover:shadow-[0_16px_40px_rgba(242,78,30,0.2)]" },
    { name: "Adobe XD", logo: "/assets/tools/adobexd.svg", tag: "Prototyping", glow: "hover:shadow-[0_16px_40px_rgba(255,97,246,0.22)]" },
    { name: "Illustrator", logo: "/assets/tools/adobeillustrator.svg", tag: "Vector & Illustration", glow: "hover:shadow-[0_16px_40px_rgba(255,154,0,0.22)]" },
    { name: "Photoshop", logo: "/assets/tools/adobephotoshop.svg", tag: "Image Editing", glow: "hover:shadow-[0_16px_40px_rgba(49,168,255,0.22)]" },
    { name: "ChatGPT", logo: "/assets/tools/openai.svg", tag: "AI Ideation", glow: "hover:shadow-[0_16px_40px_rgba(65,41,145,0.2)]" },
    { name: "Google Stitch", logo: "/assets/tools/googlestitch.svg", tag: "AI UI Concepts", glow: "hover:shadow-[0_16px_40px_rgba(66,133,244,0.22)]" },
    { name: "Gemini", logo: "/assets/tools/googlegemini.svg", tag: "AI Assistance", glow: "hover:shadow-[0_16px_40px_rgba(142,117,178,0.25)]" },
];

export default function About() {
    return (
        <section id="about" className="relative overflow-hidden bg-white py-24 lg:py-32" data-testid="about-section">
            <div className="pointer-events-none absolute -left-32 top-24 h-80 w-80 rounded-full bg-lav-100 opacity-80 blur-3xl" />
            <div className="pointer-events-none absolute -right-40 bottom-40 h-96 w-96 rounded-full bg-viol/10 blur-3xl" />

            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
                <div className="relative lg:col-span-5">
                    <Reveal>
                        <div className="relative mx-auto max-w-md">
                            <motion.div
                                animate={{ y: [0, -8, 0] }}
                                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <img
                                    src="/assets/about-portrait.png"
                                    alt="Portrait of Sushma Jaykar"
                                    className="h-auto w-full"
                                    data-testid="about-photo"
                                />
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.4, duration: 0.6 }}
                                className="glass absolute -right-2 bottom-10 flex items-center gap-2 rounded-full py-2 pl-3 pr-5 sm:right-0"
                                data-testid="about-location-chip"
                            >
                                <MapPin className="h-4 w-4 text-viol" />
                                <span className="text-xs font-bold text-ink">Mumbai, India</span>
                            </motion.div>
                            <motion.span
                                initial={{ opacity: 0, rotate: -12 }}
                                whileInView={{ opacity: 1, rotate: -8 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.55, duration: 0.6 }}
                                className="hand absolute -left-2 top-6 text-2xl text-viol sm:-left-6"
                                aria-hidden="true"
                            >
                                hello!
                            </motion.span>
                        </div>
                    </Reveal>
                </div>

                <div className="lg:col-span-7">
                    <Reveal>
                        <Eyebrow>About Me</Eyebrow>
                        <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
                            Hi, I'm Sushma.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-7 max-w-2xl space-y-5">
                            <p className="text-base leading-[1.85] text-mist">
                                I'm a UI/UX Designer with{" "}
                                <span className="rounded bg-sun-soft px-1.5 font-semibold text-ink">5+ years of experience</span>{" "}
                                crafting intuitive digital experiences across Healthcare, Banking,
                                Finance, Entertainment and E-commerce.
                            </p>
                            <p className="text-base leading-[1.85] text-mist">
                                I enjoy understanding people, exploring how products work and
                                turning complex problems into simple, thoughtful experiences.
                            </p>
                            <p className="text-base leading-[1.85] text-mist">
                                My work sits between{" "}
                                <span className="font-semibold text-ink">UX thinking</span>,{" "}
                                <span className="font-semibold text-ink">visual design</span>,{" "}
                                <span className="font-semibold text-ink">product thinking</span>,{" "}
                                <span className="font-semibold text-ink">technology</span> and{" "}
                                <span className="font-semibold text-ink">AI</span>.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>

            <div className="relative mx-auto mt-20 max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="tools-subsection">
                <Reveal>
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <Eyebrow>Toolbox</Eyebrow>
                            <h3 className="mt-3 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
                                Tools I use
                            </h3>
                        </div>
                        <p className="hand rotate-[-1.5deg] text-2xl text-viol" aria-hidden="true">
                            my everyday stack ✦
                        </p>
                    </div>
                </Reveal>
                <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    {tools.map((t, i) => (
                        <Reveal key={t.name} delay={i * 0.06}>
                            <motion.div
                                whileHover={{ y: -5, rotate: i % 2 === 0 ? -0.75 : 0.75 }}
                                transition={{ type: "spring", stiffness: 280, damping: 18 }}
                                className={`flex h-full items-center gap-3.5 rounded-2xl bg-white p-4 ring-1 ring-lav-200 shadow-[0_6px_20px_rgba(112,71,245,0.06)] transition-shadow duration-300 sm:p-5 ${t.glow}`}
                                data-testid={`tool-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <img src={t.logo} alt={`${t.name} logo`} className="h-8 w-8 shrink-0 sm:h-9 sm:w-9" loading="lazy" />
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-extrabold text-ink">{t.name}</p>
                                    <p className="mt-0.5 font-mono text-[9px] uppercase tracking-widest text-mist">{t.tag}</p>
                                </div>
                            </motion.div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
